import { useSearchBalances } from '@/api/balance/balance';
import { useGetRedeemedVouchersMonthlyStats } from '@/api/vouchers/vouchers';
import { useListProviders } from '@/api/voucher-providers/voucher-providers';
import { useListVouchers } from '@/api/vouchers/vouchers';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Tag, Heart, LineChart } from 'lucide-react';
import { VoucherProviderDetailPage } from '@/pages/admin/providers/VoucherProviderDetailPage';
import { VoucherDetailPage } from '@/pages/admin/vouchers/VoucherDetailPage';
import React from 'react';
import { Route, Routes } from 'react-router';
import { AccessControlPage } from './AccessControlPage';
import { BalanceManagementPage } from './BalanceManagementPage';
import { UserManagementPage } from './UserManagementPage';
import { VoucherCreatePage } from './VoucherCreatePage';
import { VoucherManagement } from './VoucherManagement';
import { VoucherProviderCreatePage } from './VoucherProviderCreatePage';
import { VoucherProviderManagement } from './VoucherProviderManagement';
import { AdminNotFoundPage } from './AdminNotFoundPage';

const AdminDashboard: React.FC = () => {
  const { data: balanceSearchData } = useSearchBalances({
    year: new Date().getFullYear(),
    limit: 100,
    offset: 0,
  });

  const { data: vouchersData } = useListVouchers(
    {
      is_active: true,
      per_page: 1, // We only need the total count
    },
    { query: { enabled: true } }
  );

  const { data: providersData } = useListProviders(
    {
      is_active: true,
      per_page: 1, // We only need the total count
    },
    { query: { enabled: true } }
  );

  const { data: redeemedStats } = useGetRedeemedVouchersMonthlyStats({
    query: { enabled: true },
  });

  const balanceData = balanceSearchData?.balances || [];
  const activeVouchersCount = vouchersData?.total || 0;
  const activePartnersCount = providersData?.total || 0;

  // Calculate balance statistics
  const totalUsers = balanceData.length;

  return (
    <div className='flex flex-col gap-8 items-start relative size-full'>
      {/* Header Section */}
      <div className='flex flex-col gap-10 items-start relative shrink-0 w-full'>
        <div className='flex items-end justify-between relative shrink-0 w-full'>
          <div className='flex flex-col gap-2 items-start relative shrink-0'>
            <div className='flex gap-4 items-center relative shrink-0 w-full'>
              <p className='text-swd-tuerkis-darker text-[28px] font-semibold leading-9'>Dashboard</p>
            </div>
            <p className='text-neutral-grau text-base font-medium leading-6 tracking-wide'>
              Übersicht über das SWD Gutscheinportal
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className='flex items-start relative shrink-0 w-full flex-wrap gap-4'>
        <Card className='bg-white border-neutral-hellgrau border-[0.5px] border-solid items-center px-6 py-4 rounded-[40px] shadow-none shrink-0 min-w-[289px] flex-1 max-w-[289px]'>
          <CardContent className='p-0 space-y-0 flex gap-6 items-center w-full h-full'>
            <div className='bg-swd-hellblau flex gap-2.5 h-[54px] items-center justify-center px-2 py-2 rounded-[60px] shrink-0 w-[52px]'>
              <Tag className='h-7 w-7 text-swd-tuerkis-darker' />
            </div>
            <div className='flex flex-col gap-1 items-start relative shrink-0'>
              <div className='flex flex-col font-medium justify-center leading-0 relative shrink-0 text-neutral-dunkelgrau text-base tracking-wide whitespace-nowrap'>
                <p className='leading-6'>Aktive Gutscheine</p>
              </div>
              <div className='flex flex-col gap-1 items-start justify-center relative shrink-0'>
                <div className='flex flex-col font-semibold justify-center leading-0 relative shrink-0 text-swd-tuerkis-darker text-[28px] whitespace-nowrap'>
                  <p className='leading-9'>{activeVouchersCount}</p>
                </div>
                <div className='flex gap-1 items-center leading-5 relative shrink-0 text-sm tracking-wide'>
                  <p className='font-medium relative shrink-0 text-neutral-grau'>Verfügbar</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className='bg-white border-neutral-hellgrau border-[0.5px] border-solid items-center px-6 py-4 rounded-[40px] shadow-none shrink-0 min-w-[289px] flex-1 max-w-[289px]'>
          <CardContent className='p-0 space-y-0 flex gap-6 items-center w-full h-full'>
            <div className='bg-swd-hellblau flex gap-2.5 h-[54px] items-center justify-center px-2 py-2 rounded-[60px] shrink-0 w-[52px]'>
              <Users className='h-7 w-7 text-swd-tuerkis-darker' />
            </div>
            <div className='flex flex-col gap-1 items-start relative shrink-0'>
              <div className='flex flex-col font-medium justify-center leading-0 relative shrink-0 text-neutral-dunkelgrau text-base tracking-wide whitespace-nowrap'>
                <p className='leading-6'>Registrierte Nutzer</p>
              </div>
              <div className='flex flex-col gap-1 items-start justify-center relative shrink-0'>
                <div className='flex flex-col font-semibold justify-center leading-0 relative shrink-0 text-swd-tuerkis-darker text-[28px] whitespace-nowrap'>
                  <p className='leading-9'>{totalUsers}</p>
                </div>
                <div className='flex gap-1 items-center leading-5 relative shrink-0 text-sm tracking-wide'>
                  <p className='font-bold relative shrink-0 text-alert-success'>
                    Aktive Budgets {new Date().getFullYear()}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className='bg-white border-neutral-hellgrau border-[0.5px] border-solid items-center px-6 py-4 rounded-[40px] shadow-none shrink-0 min-w-[289px] flex-1 max-w-[289px]'>
          <CardContent className='p-0 space-y-0 flex gap-6 items-center w-full h-full'>
            <div className='bg-swd-hellblau flex gap-2.5 h-[54px] items-center justify-center px-2 py-2 rounded-[60px] shrink-0 w-[52px]'>
              <Heart className='h-7 w-7 text-swd-tuerkis-darker' />
            </div>
            <div className='flex flex-col gap-1 items-start relative shrink-0'>
              <div className='flex flex-col font-medium justify-center leading-0 relative shrink-0 text-neutral-dunkelgrau text-base tracking-wide whitespace-nowrap'>
                <p className='leading-6'>Aktive Partner</p>
              </div>
              <div className='flex flex-col gap-1 items-start justify-center relative shrink-0'>
                <div className='flex flex-col font-semibold justify-center leading-0 relative shrink-0 text-swd-tuerkis-darker text-[28px] whitespace-nowrap'>
                  <p className='leading-9'>{activePartnersCount}</p>
                </div>
                <div className='flex gap-1 items-center leading-5 relative shrink-0 text-sm tracking-wide'>
                  <p className='font-medium relative shrink-0 text-neutral-grau'>Partner</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className='bg-white border-neutral-hellgrau border-[0.5px] border-solid items-center px-6 py-4 rounded-[40px] shadow-none shrink-0 min-w-[289px] flex-1 max-w-[289px]'>
          <CardContent className='p-0 space-y-0 flex gap-6 items-center w-full h-full'>
            <div className='bg-swd-hellblau flex gap-2.5 h-[54px] items-center justify-center px-2 py-2 rounded-[60px] shrink-0 w-[52px]'>
              <LineChart className='h-7 w-7 text-swd-tuerkis-darker' />
            </div>
            <div className='flex flex-col gap-1 items-start relative shrink-0'>
              <div className='flex flex-col font-medium justify-center leading-0 relative shrink-0 text-neutral-dunkelgrau text-base tracking-wide whitespace-nowrap'>
                <p className='leading-6'>
                  Einlösungen (
                  {new Date().toLocaleString('de-DE', {
                    month: 'long',
                  })}
                  )
                </p>
              </div>
              <div className='flex flex-col gap-1 items-start justify-center relative shrink-0'>
                <div className='flex flex-col font-semibold justify-center leading-0 relative shrink-0 text-swd-tuerkis-darker text-[28px] whitespace-nowrap'>
                  <p className='leading-9'>
                    {redeemedStats?.current_month_count !== undefined ? redeemedStats.current_month_count : '—'}
                  </p>
                </div>
                <div className='flex gap-1 items-center leading-5 relative shrink-0 text-sm tracking-wide'>
                  {redeemedStats?.percentage_difference !== undefined && (
                    <p
                      className={`font-medium relative shrink-0 ${
                        redeemedStats.percentage_difference >= 0 ? 'text-alert-success' : 'text-alert-error'
                      }`}
                    >
                      {redeemedStats.percentage_difference >= 0 ? '↑' : '↓'}{' '}
                      {Math.abs(redeemedStats.percentage_difference).toFixed(1)}% zum Vormonat
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const AdminPage: React.FC = () => {
  const { user } = useAuth();

  if (!user?.isAdmin) {
    return (
      <div className='container mx-auto p-6'>
        <Card>
          <CardContent className='pt-6'>
            <p className='text-center text-muted-foreground'>
              Sie haben keine Berechtigung, auf diese Seite zuzugreifen.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AdminLayout>
      <Routes>
        <Route path='/' element={<AdminDashboard />} />
        <Route path='/vouchers' element={<VoucherManagement />} />
        <Route path='/vouchers/create' element={<VoucherCreatePage />} />
        <Route path='/vouchers/:id' element={<VoucherDetailPage />} />
        <Route path='/providers' element={<VoucherProviderManagement />} />
        <Route path='/providers/create' element={<VoucherProviderCreatePage />} />
        <Route path='/providers/:id' element={<VoucherProviderDetailPage />} />
        <Route path='/balance' element={<BalanceManagementPage />} />
        <Route path='/users' element={<UserManagementPage />} />
        <Route path='/access-control' element={<AccessControlPage />} />
        <Route path='*' element={<AdminNotFoundPage />} />
      </Routes>
    </AdminLayout>
  );
};

export default AdminPage;
